import React, { useEffect, useRef, useState } from 'react';
import { Button, Card } from 'reactstrap';
import { v4 as uuidv4 } from 'uuid';

import { getApiErrorMessage, hasAdminRole } from '@/api/admin-access';
import type { CreditAccountType, CreditBalance } from '@/api/ai-credits';
import { addAiCredits, getAiCredits } from '@/api/ai-credits';
import { UserStore } from '@/stores';

const CreditControls = ({
  accountType,
  accountId,
}: {
  accountType: CreditAccountType;
  accountId: number;
}) => {
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [reload, setReload] = useState(0);
  const pending = useRef<{ signature: string; id: string } | null>(null);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    getAiCredits(accountType, accountId)
      .then((value) => {
        if (active) setBalance(value);
      })
      .catch((err) => {
        if (active) setError(getApiErrorMessage(err));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [accountType, accountId, reload]);

  const add = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSaving(true);
    setError('');
    setSuccess('');
    const signature = JSON.stringify([amount, reason.trim()]);
    if (pending.current?.signature !== signature)
      pending.current = { signature, id: uuidv4() };
    try {
      const updated = await addAiCredits(
        accountType,
        accountId,
        Number(amount),
        reason,
        pending.current.id
      );
      if (!mounted.current) return;
      setBalance(updated);
      setSuccess(`${Number(amount).toLocaleString()} credits added.`);
      setAmount('');
      setReason('');
      pending.current = null;
    } catch (err) {
      if (mounted.current) setError(getApiErrorMessage(err));
    } finally {
      inFlight.current = false;
      if (mounted.current) setSaving(false);
    }
  };
  const valid =
    /^\d+$/.test(amount) &&
    Number(amount) > 0 &&
    Number(amount) <= 1000000 &&
    Boolean(reason.trim());
  const prefix = `credits-${accountType}-${accountId}`;
  return (
    <>
      {loading ? (
        <p role="status">Loading credits…</p>
      ) : (
        balance && (
          <>
            <div style={{ fontSize: 28, fontWeight: 600 }}>
              {balance.spendable.toLocaleString(undefined, {
                maximumFractionDigits: 6,
              })}
            </div>
            <p className="text-muted">
              Available to spend ·{' '}
              {balance.reserved.toLocaleString(undefined, {
                maximumFractionDigits: 6,
              })}{' '}
              reserved
            </p>
          </>
        )
      )}
      {error && (
        <div role="alert" className="alert alert-danger">
          {error}
        </div>
      )}
      {success && (
        <div role="status" className="alert alert-success">
          {success}
        </div>
      )}
      <Button
        size="sm"
        outline
        disabled={loading || saving}
        onClick={() => setReload((value) => value + 1)}
      >
        Refresh balance
      </Button>
      <label htmlFor={`${prefix}-amount`} className="mt-3">
        Credits to add
      </label>
      <input
        id={`${prefix}-amount`}
        className="form-control"
        type="number"
        min="1"
        max="1000000"
        step="1"
        value={amount}
        disabled={saving}
        onChange={(event) => setAmount(event.target.value)}
      />
      <label htmlFor={`${prefix}-reason`} className="mt-2">
        Reason
      </label>
      <textarea
        id={`${prefix}-reason`}
        className="form-control"
        maxLength={300}
        value={reason}
        disabled={saving}
        onChange={(event) => setReason(event.target.value)}
        placeholder="Why are these credits being added?"
      />
      <Button
        className="mt-3"
        color="primary"
        disabled={!valid || !balance || loading || saving}
        onClick={add}
      >
        {saving ? 'Adding…' : 'Add credits'}
      </Button>
    </>
  );
};

const AiCredits = ({
  accountType,
  accountId,
}: {
  accountType: CreditAccountType;
  accountId?: number | null;
}) => {
  const access = UserStore.useState((state) => state.access);
  if (!hasAdminRole(access)) return null;
  return (
    <Card style={{ padding: 16, marginTop: 12 }}>
      <h5>AI credits</h5>
      {accountId ? (
        <CreditControls
          key={`${accountType}-${accountId}`}
          accountType={accountType}
          accountId={accountId}
        />
      ) : (
        <p className="text-muted mb-0">
          Save this account before managing AI credits.
        </p>
      )}
    </Card>
  );
};
export default AiCredits;
